import { useAuth } from "../../context/AuthContext";
import { useState, useRef, useEffect } from "react";
import { profileService } from "../../services/profileService";
import { toast } from "react-hot-toast";
import { ResolvedImage } from "../media/ResolvedImage";
import { resizeImageFile } from "../../utils/imageResize";

// Sovereign Emerald palette
const SE = {
  paper:         '#FAF7F0',
  raised:        'rgba(255,253,246,0.97)',
  hairline:      'rgba(11,59,46,0.14)',
  hairlineSoft:  'rgba(11,59,46,0.10)',
  title:         '#0B3B2E',
  bottle:        '#1F5A47',
  brand:         '#339274',
  champagne:     '#C9A961',
  champagneWash: 'rgba(201,169,97,0.14)',
  champagneTile: 'rgba(201,169,97,0.22)',
  champagneRing: 'rgba(201,169,97,0.50)',
  muted:         '#7C8A82',
  ink:           '#2A1F17',
};

interface UserMetaCardProps {
  completion?: number;
}

export default function UserMetaCard({ completion = 0 }: UserMetaCardProps) {
  const { user, updateUser } = useAuth();
  const [profileImage, setProfileImage] = useState<string | null>(
    user?.profilePhoto || user?.profileImage || null
  );
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedImageKey, setUploadedImageKey] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const userName =
    user?.name ||
    [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() ||
    "User";
  const userEmail    = user?.email || "";
  const userPhone    = user?.phone || user?.mobile || "";
  const userLocation =
    user?.location ||
    [user?.address?.city, user?.address?.country].filter(Boolean).join(", ") ||
    user?.address?.city || "";
  const isVerified = user?.isEmailVerified ?? user?.emailVerified ?? user?.isActive ?? false;

  const departmentRoles: Array<{ department: string; roleName: string; active: boolean }> = (() => {
    if (Array.isArray(user?.roles) && user!.roles.length > 0) {
      return user!.roles.map((r: any) => ({
        department: r.department,
        roleName:
          (typeof r.roleId === "object" ? r.roleId?.name : null) ||
          user?.role?.name || user?.roleName || "User",
        active: r.department === user?.activeDepartment,
      }));
    }
    return [{ department: user?.activeDepartment || "", roleName: user?.role?.name || user?.role || "User", active: true }];
  })();

  const social = user?.socialLinks || {};
  const socialEntries = [
    { key: "linkedin",  label: "LinkedIn",  url: social.linkedin },
    { key: "twitter",   label: "X",         url: social.twitter || social.x },
    { key: "facebook",  label: "Facebook",  url: social.facebook },
    { key: "instagram", label: "Instagram", url: social.instagram },
  ].filter((s) => !!s.url);

  useEffect(() => {
    const next = user?.profilePhoto || user?.profileImage || null;
    if (next !== profileImage) setProfileImage(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.profilePhoto, user?.profileImage]);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("File size must be less than 10MB"); return; }

    setIsUploading(true);
    try {
      const resized = await resizeImageFile(file, { maxWidth: 512, maxHeight: 512, quality: 0.85, mimeType: "image/jpeg" });
      const uploadResult = await profileService.uploadProfileImage(resized);
      if (uploadResult.success && uploadResult.data?.key) {
        const updateResult = await profileService.updateProfileImage(uploadResult.data.key);
        if (updateResult.success) {
          const serverData = (updateResult.data || {}) as { profilePhoto?: string };
          const displayUrl = serverData.profilePhoto || uploadResult.data.url;
          setProfileImage(displayUrl);
          setUploadedImageKey(uploadResult.data.key);
          updateUser({ profilePhoto: displayUrl, profileImage: displayUrl });
          toast.success("Profile photo updated");
        } else { toast.error(updateResult.message); }
      } else { toast.error(uploadResult.message || "Failed to upload image"); }
    } catch { toast.error("Failed to upload image"); }
    finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemoveImage = async () => {
    try {
      await profileService.updateProfileImage("");
      if (uploadedImageKey) {
        try { await profileService.deleteProfileImage(uploadedImageKey); }
        catch (err) { console.warn("S3 delete failed:", err); }
      }
      setProfileImage(null);
      setUploadedImageKey(null);
      updateUser({ profilePhoto: null, profileImage: null });
      toast.success("Profile photo removed");
    } catch { toast.error("Failed to remove image"); }
  };

  const completionBarColor =
    completion >= 80 ? `linear-gradient(to right, ${SE.brand}, #5b8c24)` :
    completion >= 50 ? `linear-gradient(to right, ${SE.champagne}, ${SE.brand})` :
                      `linear-gradient(to right, #e07c3c, #e0a03c)`;

  const getInitials = () => {
    const parts = userName.trim().split(/\s+/);
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : userName.charAt(0).toUpperCase();
  };

  return (
    <div
      className="relative overflow-hidden rounded-2xl"
      style={{
        backgroundColor: SE.paper,
        border: `1px solid ${SE.hairline}`,
        boxShadow: '1px 0 0 rgba(201,169,97,0.08), 0 4px 16px rgba(11,59,46,0.08), 0 12px 40px rgba(11,59,46,0.05)',
      }}
    >
      {/* ── COVER BANNER ─────────────────────────────────────────────── */}
      <div
        className="relative h-36 w-full overflow-hidden sm:h-44"
        style={{ background: 'linear-gradient(135deg, #0B3B2E 0%, #1F5A47 55%, #2d7a5e 100%)' }}
      >
        {/* Subtle guilloche-inspired rosette rings */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.07]" preserveAspectRatio="xMidYMid slice" aria-hidden>
          <defs>
            <radialGradient id="rg-glow" cx="75%" cy="30%" r="40%">
              <stop offset="0%" stopColor="#C9A961" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#C9A961" stopOpacity="0" />
            </radialGradient>
          </defs>
          <rect width="100%" height="100%" fill="url(#rg-glow)" />
          {[80, 60, 40, 22].map((r, i) => (
            <circle key={i} cx="75%" cy="30%" r={`${r}%`} fill="none" stroke="#C9A961" strokeWidth="0.5" strokeDasharray={i % 2 === 0 ? "3 6" : "1 4"} />
          ))}
        </svg>
        {/* Bottom champagne hairline */}
        <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: `linear-gradient(to right, transparent, ${SE.champagne}55, transparent)` }} />
      </div>

      <div className="px-5 pb-6 sm:px-6">
        {/* ── AVATAR + IDENTITY ────────────────────────────────────────── */}
        <div className="-mt-14 flex flex-col gap-4 sm:flex-row sm:items-end sm:gap-6">
          {/* Avatar */}
          <div className="relative group self-center sm:self-end flex-shrink-0">
            <div
              className="h-28 w-28 overflow-hidden rounded-full cursor-pointer transition-all duration-200 sm:h-32 sm:w-32"
              style={{
                border: `4px solid ${SE.paper}`,
                boxShadow: `0 0 0 2px ${SE.champagneRing}, 0 4px 20px rgba(11,59,46,0.20)`,
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              {profileImage ? (
                <ResolvedImage mediaSrc={profileImage} alt={userName} className="h-full w-full object-cover" />
              ) : (
                <div
                  className="flex h-full w-full items-center justify-center text-white"
                  style={{ backgroundImage: 'linear-gradient(135deg, #1F5A47, #0B3B2E)' }}
                >
                  <span className="text-4xl font-bold tracking-tight sm:text-5xl">{getInitials()}</span>
                </div>
              )}
            </div>

            {/* Hover overlay */}
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
              <div className="flex flex-col items-center gap-1 text-white">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-xs font-medium">Change</span>
              </div>
            </div>

            {isUploading && (
              <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/60">
                <div
                  className="h-7 w-7 animate-spin rounded-full border-2"
                  style={{ borderColor: SE.champagneRing, borderTopColor: SE.champagne }}
                />
              </div>
            )}

            {profileImage && !isUploading && (
              <button
                onClick={(e) => { e.stopPropagation(); handleRemoveImage(); }}
                className="absolute -top-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full text-white shadow-md transition-colors duration-200 hover:opacity-90"
                style={{ backgroundColor: '#B94040' }}
                title="Remove photo"
                aria-label="Remove photo"
              >
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}

            {/* Online indicator */}
            <span
              className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full"
              style={{ backgroundColor: SE.brand, boxShadow: `0 0 0 3px ${SE.paper}` }}
            />

            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
          </div>

          {/* Name + contact */}
          <div className="flex-1 min-w-0 text-center sm:pb-2 sm:text-left">
            <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-start">
              <h2 className="text-2xl font-bold tracking-tight" style={{ color: SE.title }}>
                {userName}
              </h2>
              {isVerified && (
                <span
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-bold tracking-wider uppercase"
                  style={{ backgroundColor: 'rgba(51,146,116,0.12)', color: SE.brand, border: `1px solid rgba(51,146,116,0.30)` }}
                >
                  <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Verified
                </span>
              )}
              {user?.isSuperAdmin && (
                <span
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-bold tracking-wider uppercase"
                  style={{ backgroundColor: SE.champagneWash, color: '#A07020', border: `1px solid ${SE.champagneRing}` }}
                >
                  Super Admin
                </span>
              )}
            </div>

            <div className="mt-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-[13px] sm:justify-start" style={{ color: SE.muted }}>
              {userEmail && (
                <span className="inline-flex items-center gap-1.5">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: SE.bottle }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8m-2 11H5a2 2 0 01-2-2V7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2z" />
                  </svg>
                  <a href={`mailto:${userEmail}`} className="hover:underline" style={{ color: SE.ink }}>{userEmail}</a>
                </span>
              )}
              {userPhone && (
                <span className="inline-flex items-center gap-1.5">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: SE.bottle }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 5a2 2 0 012-2h2.28a1 1 0 01.95.68l1.5 4.49a1 1 0 01-.5 1.21l-1.7.85a11 11 0 005.52 5.52l.85-1.7a1 1 0 011.21-.5l4.49 1.5a1 1 0 01.68.95V19a2 2 0 01-2 2h-1C9.72 21 3 14.28 3 6V5z" />
                  </svg>
                  <a href={`tel:${userPhone}`} className="hover:underline" style={{ color: SE.ink }}>{userPhone}</a>
                </span>
              )}
              {userLocation && (
                <span className="inline-flex items-center gap-1.5">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: SE.bottle }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {userLocation}
                </span>
              )}
            </div>

            {/* Department/role badges */}
            {departmentRoles.length > 0 && (
              <div className="mt-3 flex flex-wrap justify-center gap-2 sm:justify-start">
                {departmentRoles.map((dr, idx) => (
                  <span
                    key={`${dr.department}-${idx}`}
                    className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-bold tracking-[0.07em] uppercase"
                    style={
                      dr.active
                        ? { backgroundColor: SE.champagneWash, color: SE.bottle, border: `1px solid ${SE.champagneRing}` }
                        : { backgroundColor: 'rgba(11,59,46,0.05)', color: SE.muted, border: `1px solid ${SE.hairline}` }
                    }
                  >
                    <span
                      className="h-1.5 w-1.5 flex-shrink-0 rounded-full"
                      style={{ backgroundColor: dr.active ? SE.champagne : SE.muted }}
                    />
                    {dr.roleName}
                    {dr.department && <span className="opacity-60">· {dr.department}</span>}
                    {dr.active && (
                      <svg className="h-3 w-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 12 12">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2 6l3 3 5-5" />
                      </svg>
                    )}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Social links */}
          {socialEntries.length > 0 && (
            <div className="flex justify-center gap-2 sm:flex-col sm:items-end sm:justify-end sm:pb-2">
              <div className="flex gap-1.5">
                {socialEntries.map((s) => (
                  <a
                    key={s.key}
                    href={s.url as string}
                    target="_blank"
                    rel="noreferrer"
                    title={s.label}
                    className="flex h-8 w-8 items-center justify-center rounded-full transition-all duration-200"
                    style={{ backgroundColor: SE.raised, color: SE.muted, border: `1px solid ${SE.hairline}` }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLElement).style.backgroundColor = SE.champagneWash;
                      (e.currentTarget as HTMLElement).style.borderColor = SE.champagneRing;
                      (e.currentTarget as HTMLElement).style.color = SE.bottle;
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLElement).style.backgroundColor = SE.raised;
                      (e.currentTarget as HTMLElement).style.borderColor = SE.hairline;
                      (e.currentTarget as HTMLElement).style.color = SE.muted;
                    }}
                  >
                    <SocialIcon name={s.key} />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── PROFILE COMPLETION ───────────────────────────────────────── */}
        <div
          className="mt-6 rounded-xl p-4"
          style={{ backgroundColor: SE.raised, border: `1px solid ${SE.hairlineSoft}` }}
        >
          <div className="mb-2.5 flex items-center justify-between">
            <div>
              <p className="text-[12.5px] font-semibold tracking-tight" style={{ color: SE.title }}>
                Profile Completion
              </p>
              <p className="text-[11px] mt-0.5" style={{ color: SE.muted }}>
                {completion >= 100
                  ? "Your profile is fully set up."
                  : "Add remaining details to complete your profile."}
              </p>
            </div>
            <span
              className="text-xl font-bold tracking-tight"
              style={{ color: completion >= 80 ? SE.brand : completion >= 50 ? SE.champagne : '#e07c3c' }}
            >
              {completion}%
            </span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full" style={{ backgroundColor: 'rgba(11,59,46,0.10)' }}>
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: `${Math.max(2, Math.min(100, completion))}%`, background: completionBarColor }}
            />
          </div>
          {/* Tick marks at 25/50/75/100 */}
          <div className="relative mt-1 flex justify-between px-px">
            {[25, 50, 75, 100].map((tick) => (
              <span key={tick} className="text-[9px] font-medium" style={{ color: completion >= tick ? SE.champagne : SE.muted }}>
                {tick}%
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function SocialIcon({ name }: { name: string }) {
  switch (name) {
    case "linkedin":
      return <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.76 0-5 2.24-5 5v14c0 2.76 2.24 5 5 5h14c2.76 0 5-2.24 5-5v-14c0-2.76-2.24-5-5-5zm-11 19h-3v-9h3v9zm-1.5-10.27c-.97 0-1.75-.79-1.75-1.76s.78-1.75 1.75-1.75 1.75.78 1.75 1.75c0 .97-.78 1.76-1.75 1.76zm12.5 10.27h-3v-4.6c0-1.1-.02-2.52-1.54-2.52-1.54 0-1.78 1.2-1.78 2.44v4.68h-3v-9h2.88v1.23h.04c.4-.76 1.39-1.56 2.86-1.56 3.06 0 3.62 2.01 3.62 4.62v4.71z" /></svg>;
    case "twitter":
      return <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2H21l-6.52 7.45L22 22h-6.86l-4.6-5.74L4.86 22H2.1l6.97-7.97L2 2h6.95l4.16 5.32L18.244 2zm-1.2 18h1.69L7.05 4H5.27l11.77 16z" /></svg>;
    case "facebook":
      return <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>;
    case "instagram":
      return <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98C.014 8.333 0 8.741 0 12s.014 3.667.072 4.947c.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24s3.667-.014 4.947-.072c4.354-.2 6.782-2.618 6.979-6.98.059-1.28.074-1.689.074-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.667.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg>;
    default: return null;
  }
}
